import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo } from 'react';

import Comments from 'components/Comments';
import { useCommentsRoot } from 'components/Comments/Root';

import { getOfferCommentsList } from 'services/comment';

import OfferAuthorsCard from './OfferAuthorsCard';

const EmptyCommentsList = dynamic(() => import('./EmptyCommentsList'));

/**
 * Empty state só deve aparecer DEPOIS do card de autor, não antes. Por isso
 * renderizamos manualmente aqui (observando o estado do Comments.Root) em vez
 * de passar como `emptyListComponent` do Comments.Header — que injetaria o
 * empty acima do card.
 */
const EmptyCommentsPlaceholder = () => {
  const { isCommentsListEmpty } = useCommentsRoot();

  if (!isCommentsListEmpty) return null;

  return <EmptyCommentsList />;
};

const OfferCommentsList = ({
  aliasId = null,
  authors,
  authorCount,
  className,
  offerCommentsCount,
  offerId,
  offerInstructions,
  offerPublished,
  initialComments,
  userId,
  userName,
  userSpecialtyName,
  userUsername,
}) => {
  const hasAuthor =
    Array.isArray(authors) && authors.length > 0 && Boolean(userName);

  /**
   * O card de autor e o empty state vão COMO HEADER do Comments.List (dentro
   * do container com scroll). Assim eles rolam junto com os comentários e
   * somem ao rolar pra baixo, voltando ao rolar pra cima — comportamento
   * similar ao Pelando.
   */
  const scrollableHeader = (
    <>
      {hasAuthor && (
        /*
          `my-3` dá respiro entre o TabNav acima e o primeiro comentário
          abaixo. Sem isso o card de autor ficava grudado tanto nas abas
          quanto no primeiro comentário (o `gap-y` da lista foi removido
          pra replicar o visual do Pelando, então precisamos reservar o
          espaçamento por item).
        */
        <div className="my-3">
          <OfferAuthorsCard
            authorCount={authorCount}
            authors={authors}
            offerId={offerId}
            offerInstructions={offerInstructions}
            offerPublished={offerPublished}
            userId={userId}
            userName={userName}
            userSpecialtyName={userSpecialtyName}
            userUsername={userUsername}
          />
        </div>
      )}
      <EmptyCommentsPlaceholder />
    </>
  );

  return (
    /*
      O antigo `<Comments.Header>` foi removido porque o único conteúdo
      que ele carregava (o select de ordenação) migrou pro `TabNav` em
      `CommentsAndReviewsTabs` como `Comments.IconSort`. Isso liberou
      uma linha inteira acima da lista — mesma pegada do Pelando, onde
      título da aba e ordenação dividem a mesma linha.
    */
    <Comments.Section
      className={`min-h-0 flex-col gap-3 lg:flex-1 ${className ?? ''}`}
    >
      <Comments.List
        aliasId={aliasId}
        className="min-h-0 flex-1 lg:basis-0"
        commentsCount={offerCommentsCount}
        header={scrollableHeader}
        initialComments={initialComments}
        queryFn={getOfferCommentsList}
        canCommentsBeRecommendedAsReview
      />
    </Comments.Section>
  );
};

OfferCommentsList.propTypes = {
  aliasId: PropTypes.number,
  authors: PropTypes.arrayOf(PropTypes.shape()),
  authorCount: PropTypes.number,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  initialComments: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  offerCommentsCount: PropTypes.number.isRequired,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string,
  offerPublished: PropTypes.string,
  userId: PropTypes.number,
  userName: PropTypes.string,
  userSpecialtyName: PropTypes.string,
  userUsername: PropTypes.string,
};

export default memo(OfferCommentsList);
